//#region import libraries
import React, { useCallback, useEffect, useMemo, useState, useRef } from "react";
import { createPortal } from "react-dom";

// custom components
import ActionsModal from "_components/molecules/Modals/actions-modal";
import EmployeeModal from "./employee-modal";
import EmployeeProfilePicture from "./employee-profile-picture";
import AddEmployeeShift from "./add-employee-shift";

// redux
import { useDispatch, useSelector } from "react-redux";
import { selectEmployeeConversations, setSelectedEmployeeProfile, setSelectedForChat, updateEmployee } from "_redux/slices/timeline/employees";
import { selectBreakTaskQueryRange } from "_redux/slices/timeline/break-tasks";

// styling
import { Colors, Spacing } from "_styles";

// icons
import { convertTimeStampToDate } from "_utils/functions";
import { FiCoffee, FiLogIn, FiLogOut } from "react-icons/fi";
import { FiChevronRight } from "react-icons/fi";
import { IoChatbubble } from "react-icons/io5";
import { PiNotepadFill } from "react-icons/pi";
import { selectFirebaseInfo, selectFirebaseUser } from "_redux/slices/main-screen/user";
import { getStaffEquipments, selectStaffEquipments } from "_redux/slices/timeline/staff-equipments";

//#endregion
// get shift hours and minutes (stable helper)
const getShiftHoursAndMinutes = (date) => {
  const hours = String(new Date(convertTimeStampToDate(date)).getHours()).padStart(2, '0');
  const minutes = String(new Date(convertTimeStampToDate(date)).getMinutes()).padStart(2, '0');
  return `${hours}:${minutes}`;
};

const EmployeeInformation = (props) => {
  //#region redux
  const dispatch = useDispatch();
  const employeeConversations = useSelector(selectEmployeeConversations);
  const dbUser = useSelector(selectFirebaseInfo);
  const equipmentData = useSelector(selectStaffEquipments);
  const empBreakSummary = useSelector(state => state.breakTasks?.byEmployeeId?.[props?.employee?.employeeId] ?? null);
  const breakQueryRange = useSelector(selectBreakTaskQueryRange);
  //#endregion

  //#region state (only UI state that can't be derived)
  const [showEmployeeModal, setShowEmployeeModal] = useState(false);
  const [displayShiftModal, setDisplayShiftModal] = useState(false);
  const [contextMenu, setContextMenu] = useState(null);
  const [breakTooltip, setBreakTooltip] = useState(null);
  const contextMenuRef = useRef(null);
  const breakTooltipTimer = useRef(null);
  //#endregion


  //#region derived values (no effect, no extra render)
  const employee = props?.employee;
  const convId = useMemo(
    () => (employee?.employeeId && employee?.airport ? `${employee.employeeId}_${employee.airport}` : null),
    [employee?.employeeId, employee?.airport]
  );

  const employeeStatus = useMemo(
    () => (employee?.status ? employee.status.charAt(0).toUpperCase() + employee.status.slice(1) : "Offline"),
    [employee?.status]
  );

  const shiftTimes = useMemo(() => {
    if (!employee?.shift?.end || employee?.status === "offline") return null;
    const start = getShiftHoursAndMinutes(employee?.shift?.start) ?? "";
    const end = getShiftHoursAndMinutes(employee?.shift?.end) ?? "";
    return [start, end];
  }, [employee?.shift?.start, employee?.shift?.end, employee?.status]);

  const shouldGlow = useMemo(() => {
    //if (dbUser?.details?.authority === "JER") return false;
    if (employee?.status === "offline" || !convId) return false;
    if (!Array.isArray(employeeConversations) || !employeeConversations.length) return false;
    return employeeConversations.some(
      (conv) =>
        conv?._id === convId &&
        conv?.lastMessage?.status !== "seen" &&
        (conv?.lastMessage?.senderId == employee?.employeeId || conv?.lastMessage?.senderId == employee?.userId)
    );
  }, [dbUser?.details?.authority, employee?.status, convId, employeeConversations]);
  //#endregion

  //#region callbacks (stable references)
  const handleEmployeeClick = useCallback(() => {
    setContextMenu(null);
    if (!equipmentData.vehicleList?.length) {
      dispatch(getStaffEquipments(dbUser.details.authority));
    }
    dispatch(setSelectedEmployeeProfile(employee));
    setShowEmployeeModal(true);
  }, [equipmentData.vehicleList?.length, dbUser.details?.authority, employee, dispatch]);

  const turnEmployeeStatus = useCallback(() => {
    setContextMenu(null);
    if (!employee?.userId) return;
    const newStatus = employee.status === "online" ? "offline" : "online";
    dispatch(updateEmployee({ userId: employee.userId, body: { status: newStatus } }));
  }, [employee?.userId, employee?.status, dispatch]);

  const handleOpenChat = useCallback(() => {
    setContextMenu(null);
    //if (dbUser?.details?.authority === "JER") return;
    const conversation = convId
      ? (employeeConversations?.find((c) => c?._id === convId) ?? null)
      : null;
    dispatch(setSelectedForChat({ employee, conversation }));
  }, [dbUser?.details?.authority, convId, employee, employeeConversations, dispatch]);

  const handleOpenOptions = useCallback((e) => {
    e.preventDefault();
    setContextMenu((prev) =>
      prev === null ? { mouseX: e.clientX, mouseY: e.clientY } : null
    );
  }, []);

  const openContextAt = useCallback((e) => {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const openShiftModal = useCallback(() => setDisplayShiftModal(true), []);
  const closeEmployeeModal = useCallback(() => setShowEmployeeModal(false), []);
  const closeShiftModal = useCallback(() => setDisplayShiftModal(false), []);
  //#endregion

  // Close contextMenu when clicked outside
  useEffect(() => {
    if (!contextMenu) return;

    const onDown = (e) => {
      if (contextMenuRef.current && !contextMenuRef.current.contains(e.target)) {
        setContextMenu(null);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setContextMenu(null);
    };

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [contextMenu]);

  // check if the user has taken a break for their shift


  return (
    <>
      <div
        onClick={() => { }}
        onContextMenu={handleOpenOptions}
        style={{
          opacity: employee?.status === "offline" ? 0.6 : 1.0,
          width: "100%",
          height: '100%',
          position: "relative",
          cursor: "pointer",
          display: "flex",
          flex: 1,
          overflow: "hidden",
          borderStyle: props?.hideBorder ? "none" : 'solid',
          borderWidth: props?.hideBorder ? 0 : 1.5,
          borderColor: props?.hideBorder ? "transparent" : 'lightgray',
        }}>

        {/* profile picture + name */}
        <div onClick={openContextAt} style={{ flex: 1, minWidth: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: Spacing.SCALE_8 }}>

          {/* employee circle */}
          <EmployeeProfilePicture
            key={employee.employeeId}
            employee={employee}
            employeeStatus={employeeStatus}
            showStatus={true}
            showName={true}
            shift={shiftTimes}
            glow={shouldGlow}
          />

        </div>

        {/* icons column: break + notification stacked vertically */}
        {(empBreakSummary?.hasBreakTaken || shouldGlow) && (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, paddingRight: Spacing.SCALE_8, paddingLeft: 2, flexShrink: 0 }}>
            {empBreakSummary?.hasBreakTaken && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: 4, position: 'relative' }}
                onClick={(e) => { e.stopPropagation(); props?.openBreakTaskModal?.(); }}
                onMouseEnter={(e) => {
                  const rect = e.currentTarget.getBoundingClientRect();
                  clearTimeout(breakTooltipTimer.current);
                  setBreakTooltip({ x: rect.right, y: rect.top + rect.height / 2 });
                }}
                onMouseLeave={() => {
                  breakTooltipTimer.current = setTimeout(() => setBreakTooltip(null), 150);
                }}
              >
                <FiCoffee size={14} color={Colors.PRM_LIGHT_BLUE} />
                {empBreakSummary.breakCount > 1 && (
                  <span style={{ fontSize: 10, color: 'grey', fontWeight: 'bold' }}>
                    x{empBreakSummary.breakCount}
                  </span>
                )}
              </div>
            )}
            {shouldGlow && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }} onClick={handleOpenChat}>
                <div style={{ width: 15, height: 15, borderRadius: '50%', backgroundColor: Colors.PRM_LIGHT_BLUE }} />
              </div>
            )}
          </div>
        )}

      </div>

      {/* break activity tooltip */}
      {breakTooltip && empBreakSummary?.breaks?.length > 0 &&
        createPortal(
          <div
            onMouseEnter={() => clearTimeout(breakTooltipTimer.current)}
            onMouseLeave={() => setBreakTooltip(null)}
            style={{
              position: 'fixed',
              top: breakTooltip.y,
              left: breakTooltip.x + 8,
              transform: 'translateY(-50%)',
              background: 'white',
              border: '1px solid lightgray',
              borderRadius: 8,
              padding: '10px 14px',
              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
              zIndex: 999999999,
              minWidth: 180,
              pointerEvents: 'auto',
            }}
          >
            <div style={{ fontSize: 12, fontWeight: 'bold', color: '#333', marginBottom: 4, borderBottom: '1px solid #eee', paddingBottom: 6 }}>
              Break Activity ({empBreakSummary.breakCount})
            </div>
            {breakQueryRange && (
              <div style={{ fontSize: 10, color: '#999', marginBottom: 8 }}>
                {new Date(breakQueryRange?.start).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                {' to '}
                {new Date(breakQueryRange?.end).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            {empBreakSummary.breaks.map((b, i) => (
              <div key={b.id || i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                <FiCoffee size={12} color={Colors.PRM_LIGHT_BLUE} />
                <span style={{ fontSize: 11, color: '#555' }}>
                  {(() => {
                    const startDate = b?.breakTaskInformation?.breakStartTime?.toDate?.();
                    const endDate = b?.breakTaskInformation?.breakEndTime?.toDate?.();
                    const fmtTime = (d) => d?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) ?? '—';
                    const fmtDate = (d) => d ? `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}` : '';
                    return `${fmtTime(startDate)} – ${fmtTime(endDate)} (${fmtDate(startDate)})`;
                  })()}
                </span>
              </div>
            ))}
          </div>,
          document.body
        )
      }

      {/* Employee context options Modal */}
      {contextMenu &&
        createPortal(
          <div
            ref={contextMenuRef}
            style={{
              position: "fixed",
              top: contextMenu.y,
              left: contextMenu.x,
              transform: "translate(10px, 10px)",
              background: "white",
              border: "1px solid lightgray",
              borderRadius: 6,
              padding: 10,
              boxShadow: "0 2px 6px rgba(0,0,0,0.15)",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 12,
              zIndex: 999999999,
              width: 165,
              height: "auto",
              maxHeight: "none",
              overflow: "visible",
              pointerEvents: "auto",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* employee details */}
            <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={handleEmployeeClick}>
              <FiChevronRight
                onClick={handleEmployeeClick}
                style={{ cursor: "pointer" }}
                color={Colors.PRM_LIGHT_BLUE}
                size={20}
                data-tippy-content="Employee Details"
              />
              <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}>Employee Details</label>
            </div>

            {employee?.status !== "offline" && (
              <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={handleOpenChat}>

                <IoChatbubble
                  onClick={handleOpenChat}
                  style={{ cursor: "pointer" }}
                  color={Colors.PRM_LIGHT_BLUE}
                  size={20}
                  data-tippy-content="Employee Chat"
                />
                <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}>Employee Chat</label>
              </div>
            )}

            {/* create break */}
            {props?.employeeShift && (
              <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={() => { setContextMenu(null); props?.openBreakTaskModal?.(); }}>
                <FiCoffee
                  color={"lightgrey"}
                  size={20}
                />
                <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}> Create Break </label>
              </div>
            )}

            {/* create adhoc task */}
            {props?.openAdhocTaskModal && (
              <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={() => { setContextMenu(null); props?.openAdhocTaskModal?.(); }}>
                <PiNotepadFill
                  color={"lightgrey"}
                  size={20}
                />
                <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}> Create Adhoc Task </label>
              </div>
            )}

            {/* log out */}
            {employee?.status !== "offline" && (
              <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={turnEmployeeStatus}>
                <FiLogOut
                  onClick={turnEmployeeStatus}
                  style={{ cursor: "pointer" }}
                  color={"red"}
                  size={20}
                  data-tippy-content="Log Employee Out"
                />
                <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', textAlign: 'left' }}> Log Out </label>
              </div>
            )}

            {/* log in */}
            {employee?.status === "offline" && (
              <div style={{ display: 'flex', cursor: 'pointer', width: '100%', gap: '1em', padding: '5px 0' }} onClick={() => { setContextMenu(null); openShiftModal(); }}>
                <FiLogIn
                  color={"green"}
                  size={20}
                />
                <label style={{ fontSize: "12px", fontWeight: 'bold', color: 'grey', cursor: 'pointer', }}> Log In </label>
              </div>
            )}
          </div>,
          document.body
        )
      }

      {/* Employee Overview Modal */}
      <ActionsModal
        title="Employee Overview"
        subtitle="Detailed profile and analytics"
        show={showEmployeeModal}
        handleClose={closeEmployeeModal}
        isTimeline={true}
      >
        <EmployeeModal closeEmployeeModal={closeEmployeeModal} />
      </ActionsModal>

      {/* Employee Shift Modal */}
      <ActionsModal
        title="Employee shift"
        subtitle="Select the start and end of employee's shift,you can cheese predefined start/end shift or custom shifts"
        show={displayShiftModal}
        handleClose={closeShiftModal}
        isTimeline={true}
      >
        <AddEmployeeShift closeModal={closeShiftModal} employee={employee} />
      </ActionsModal>
    </>
  );
};

export default React.memo(EmployeeInformation, (prev, next) => {
  return prev.employee?.employeeId === next.employee?.employeeId &&
    prev.employee?.status === next.employee?.status &&
    prev.employee?.shift?.start === next.employee?.shift?.start &&
    prev.employee?.shift?.end === next.employee?.shift?.end &&
    prev.index === next.index;
});
